import { initializeClient } from '../../modules/initializeClient';
import { isServerSideRendering } from '../../utils/isServerSideRendering';
import { createCore } from '../core/createCore';
import { setDefaultClient } from '../defaultClient';
import type { DynamicClient, DynamicClientConfig } from '../types';

/**
 * Creates a new DynamicClient instance.
 *
 * Notice the `autoInitialize` flag is true by default (unless you're running
 * in SSR), so the client will be automatically initialized when created — if
 * you want to manually initialize the client, you can set the `autoInitialize`
 * flag to false and then later call the `initializeClient` function.
 *
 * Manually calling `initializeClient` also allows you to catch any potential
 * errors that may occur during initialization.
 *
 * @param config - The configuration for the Dynamic client, like the environment ID, app url, etc.
 * @returns The Dynamic client instance.
 */
export const createDynamicClient = (
  config: DynamicClientConfig
): DynamicClient => {
  const core = createCore(config);

  const client = {
    get __core() {
      return core;
    },
    get initStatus() {
      return core.state.get().initStatus;
    },
    get mfaToken() {
      return core.state.get().mfaToken;
    },
    get projectSettings() {
      return core.state.get().projectSettings;
    },
    get sessionExpiresAt() {
      return core.state.get().sessionExpiresAt;
    },
    get token() {
      return core.state.get().token;
    },
    get user() {
      return core.state.get().user;
    },
  };

  // Define if SDK should auto initialize, default to true
  const autoInitialize = config.autoInitialize ?? true;

  // Prevent the auto initialization in SSR
  const shouldAutoInitialize = isServerSideRendering() ? false : autoInitialize;

  core.logger.debug('[createDynamicClient] Creating client...');

  if (shouldAutoInitialize) {
    core.logger.debug('[createDynamicClient] Initializing client...');
    void initializeClient(client);
  }

  setDefaultClient(client);

  return client;
};
